// ABOUTME: Notification ribbon for pending Responsibilities list approvals
// ABOUTME: Shows at top of parent view, smooth scrolls to first approval button on click

import React, { useState, useEffect } from 'react';
import { Alert, Chip } from '@mui/material';
import { ChoreListType, ChoreListStatus } from '../types/api';
import type { ChoreListPeriodStatus } from '../types/api';

interface ResponsibilitiesApprovalRibbonProps {
  childChoreStatuses: Record<number, ChoreListPeriodStatus[]>;
}

const ResponsibilitiesApprovalRibbon: React.FC<ResponsibilitiesApprovalRibbonProps> = ({ childChoreStatuses }) => {
  const [isDismissed, setIsDismissed] = useState(false);

  // Count pending approvals across all children — only Regular lists with RequiresApproval
  const pendingCount = Object.values(childChoreStatuses)
    .flat()
    .filter(list =>
      list.type === ChoreListType.Regular &&
      list.requiresApproval &&
      list.status === ChoreListStatus.PendingApproval
    )
    .length;

  // Reset dismiss state when pending count changes
  useEffect(() => {
    if (pendingCount > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentionally resets dismiss state when new items appear
      setIsDismissed(false);
    }
  }, [pendingCount]);

  if (pendingCount === 0 || isDismissed) return null;

  const handleClick = () => {
    // Scroll to first responsibilities approval button
    const approvalButton = document.querySelector('[data-responsibilities-approval-button]');
    if (approvalButton) {
      approvalButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <Alert
      data-testid="responsibilities-approval-ribbon"
      severity="info"
      variant="filled"
      sx={{ mb: 3, cursor: 'pointer' }}
      onClick={handleClick}
      onClose={() => setIsDismissed(true)}
      action={
        <Chip
          label={pendingCount}
          size="small"
          sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}
        />
      }
    >
      {pendingCount === 1
        ? '1 responsibilities list ready for approval!'
        : `${pendingCount} responsibilities lists ready for approval!`
      }
    </Alert>
  );
};

export default ResponsibilitiesApprovalRibbon;
